import React, { useCallback } from 'react';
import { Tooltip, makeStyles } from '@material-ui/core';
import { ClassType } from '../../api';
import { useHotkeys } from 'react-hotkeys-hook';
import ExitIcon from '@material-ui/icons/ExitToApp';
import TeamColorPicker from './TeamColorPicker';
import { useUpdateSession } from './useUpdateSession';
import { TSession } from './Sessions';

const useStyles = makeStyles((theme) => ({
  classTypeBox: {
    backgroundColor: '#eee',
    margin: '2px',
    padding: '8px',
    border: '1px solid #888',
    opacity: '66%',
    minHeight: '40px',
    display: ' inline-flex',
    alignItems: 'center',
    borderRadius: '6px',
  },
  selectedClassTypeBox: {
    backgroundColor: '#ccc',
    margin: '2px',
    fontWeight: 'bold',
    padding: '7px',
    border: '2px solid #000',
    minHeight: '40px',
    display: ' inline-flex',
    alignItems: 'center',
    borderRadius: '6px',
  },
  disabledClassTypeBox: {
    backgroundColor: '#756b6b',
    color: '#ccc',
    margin: '2px',
    padding: '8px',
    border: '1px solid #8d1f1f',
    opacity: '20%',
    minHeight: '40px',
    display: ' inline-flex',
    alignItems: 'center',
    borderRadius: '6px',
  },
  classTypeCounter: {
    borderLeft: '1px solid',
    marginLeft: '8px',
    padding: '4px',
    textAlign: 'center',
  },
  icon: {
    width: '22px',
    height: '22px',
    margin: '4px',
  },
}));

type Props = {
  session: TSession | undefined;
  label: string;
  count: number;
  requiredCount: number;
  color: string;
  classType: ClassType;
  isActive: boolean;
  isEnabled: boolean;
  isSubstitution: boolean;
  hotkey: string;
  setClassType: (classType: ClassType) => void;
};

const SessionTeamSelectLabel = (props: Props) => {
  const classes = useStyles();
  const [updateSession] = useUpdateSession();

  const { label, count, requiredCount, color, classType, isActive, isEnabled, hotkey, setClassType, isSubstitution } =
    props;

  const setThisClassType = useCallback(() => {
    if (!isEnabled) return;

    setClassType(classType);
  }, [setClassType, classType, isEnabled]);

  useHotkeys(hotkey, setThisClassType, { keydown: true, keyup: false }, [setThisClassType]);

  return (
    <Tooltip title={'[ ' + hotkey + ' ] Select Class'} arrow>
      <span
        onClick={setThisClassType}
        className={
          isEnabled ? (isActive ? classes.selectedClassTypeBox : classes.classTypeBox) : classes.disabledClassTypeBox
        }
        style={isActive ? { borderBottom: '6px solid ' + color } : {}}>
        <TeamColorPicker
          color={color}
          setColor={(newColor: string) => {
            if(props.session) {
              switch(classType) {
                case ClassType.Referee:
                  updateSession({ refereeColor: newColor }, props.session);
                  break;
                case ClassType.HomeKeeper:
                  updateSession({ homeKeeperColor: newColor }, props.session);
                  break;
                case ClassType.HomePlayer:
                  updateSession({ homePlayerColor: newColor }, props.session);
                  break;
                case ClassType.AwayKeeper:
                  updateSession({ awayKeeperColor: newColor }, props.session);
                  break;
                case ClassType.AwayPlayer:
                  updateSession({ awayPlayerColor: newColor }, props.session);
                  break;
              }
            }
          }}
        />
        {label}
        {isSubstitution && <ExitIcon />}
        <span className={classes.classTypeCounter}>
          {count} / {requiredCount}
        </span>
      </span>
    </Tooltip>
  );
};

export default SessionTeamSelectLabel;
